import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppShell from './components/AppShell';
import LibraryPage from './pages/LibraryPage';
import LinkCreationPage from './pages/LinkCreationPage';
import PublicSharePage from './pages/PublicSharePage';
import SharesPage from './pages/SharesPage';
import AdminPage from './pages/AdminPage';
import { assetsService } from './services/assetsService';
import { authService } from './services/authService';
import { formService } from './services/formService';
import { interactionService } from './services/interactionService';
import { itemFormService } from './services/itemFormService';
import { linksService } from './services/linksService';
import { linkAnalyticsService } from './services/linkAnalyticsService';
import { orderService } from './services/orderService';
import { shareService } from './services/shareService';
import { storageService } from './services/storageService';
import { addAssetsToGrid, removeAssetFromGrids } from './utils/assetGrids';
import { optimiseImageFile } from './utils/imageOptimisation';
import { DEFAULT_SHARE_ORIGIN } from './utils/shareLinks';

const FREE_PLAN_UPLOAD_LIMIT_BYTES = 5 * 1024 * 1024;

const isFreeSubscription = (user) => (user?.profile?.subscriptionPlan || 'free') === 'free';
const isAdminUser = (user) => user?.profile?.role === 'admin' || user?.role === 'admin';
const canOpenOriginalLink = (user, link) => Boolean(user?.uid && link?.ownerId === user.uid);

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const toDatetimeLocalValue = (date) => {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const formatFileSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const getAssetUploadBytes = (asset) => {
  if (asset?.file?.size) return asset.file.size;
  const galleryBytes = (asset?.images || []).reduce((total, image) => (
    total + (image.file?.size || Number(image.uploadBytes) || 0)
  ), 0);
  if (asset?.uploadBytes) return (Number(asset.uploadBytes) || 0) + galleryBytes;
  const sizeMatch = typeof asset?.size === 'string' ? asset.size.match(/^([\d.]+)\s*MB$/i) : null;
  return (sizeMatch ? Number(sizeMatch[1]) * 1024 * 1024 : 0) + galleryBytes;
};

const getLinkUploadBytes = (link, pendingFiles = {}) => {
  const assetBytes = (link?.assets || []).reduce((total, asset) => (
    total + getAssetUploadBytes(asset) + (asset.iconFile?.size || 0) + (Number(asset.iconUploadBytes) || 0)
  ), 0);
  const savedBrandBytes = ['icon', 'logo', 'ogImage'].reduce((total, field) => {
    const uploadBytesField = `${field}UploadBytes`;
    return total + (pendingFiles[field] ? 0 : Number(link?.[uploadBytesField]) || 0);
  }, 0);
  const pendingBrandBytes = Object.values(pendingFiles).reduce((total, file) => total + (file?.size || 0), 0);
  return assetBytes + savedBrandBytes + pendingBrandBytes;
};

const getPendingUploadBytes = (pendingFiles = {}) => (
  Object.values(pendingFiles).reduce((total, file) => total + (file?.size || 0), 0)
);

const orderStatusCanAffectStock = (order = {}, status = order.status) => {
  const normalisedStatus = String(status || '').trim();
  return normalisedStatus === 'Confirmed' || (order.source === 'pos' && normalisedStatus === 'Fulfilled');
};

const applyFreePlanLinkLimits = (link) => {
  const createdAt = link.createdAt ? new Date(link.createdAt) : null;
  const expiryBaseDate = createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt : new Date();

  return {
    ...link,
    expiry: '14 days',
    endsAt: toDatetimeLocalValue(addDays(expiryBaseDate, 14)),
  };
};

const buildDraftLink = (user, count = 0) => ({
  title: 'New Sharabl Asset Link',
  category: 'album',
  description: 'Describe what this controlled link contains and who it is intended for.',
  renderFont: 'Inter',
  backgroundColor: '#f7f9fb',
  headerBackgroundColor: '#ffffff',
  assetCardBackgroundColor: '#ffffff',
  contactTel: '',
  contactWhatsApp: '',
  contactEmail: '',
  price: '',
  currency: 'ZAR',
  owner: user?.displayName || user?.email || 'Workspace',
  ownerId: user?.uid || '',
  slug: `asset-link-${count + 1}`,
  useCustomDomain: false,
  customDomain: '',
  expiry: '14 days',
  startsAt: '',
  endsAt: '',
  protection: 'Invite only',
  inviteRequestMode: 'request',
  allowDownloads: false,
  allowSharing: false,
  assetFields: {
    title: true,
    description: true,
    price: true,
    quantity: true,
    area: true,
    sizes: false,
    colors: false,
    link: false,
    multipleImages: false,
  },
  layout: 'masonry',
  showHeroSection: true,
  showCreateLinkButton: true,
  publicPageWidth: '6xl',
  galleryColumns: 3,
  assetGrids: [],
  status: 'Draft',
  demoMode: false,
  icon: null,
  logo: null,
  logoHeight: 48,
  ogImage: null,
  assets: [],
  adminIds: [],
  managerIds: [],
  collaborators: [],
  form: {
    enabled: false,
    title: 'Share your details',
    description: 'Complete this form when you view these assets.',
    buttonLabel: 'Response form',
    submitLabel: 'Submit response',
    fields: [
      { id: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Your name' },
      { id: 'email', label: 'Email', type: 'email', required: true, placeholder: 'you@example.com' },
    ],
  },
  itemForms: [],
  checkout: {
    enableRestaurantOrdering: false,
    enableOnlineCheckout: true,
    enablePosMode: false,
    cartButtonLabel: 'Add to cart',
    drawerEyebrow: 'Store checkout',
    drawerTitle: 'Cart',
    emptyCartMessage: 'Add store items to start an order.',
    customerDetailsLabel: 'Customer details',
    formTitle: 'Send order',
    nameLabel: 'Name',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    notesLabel: 'Notes',
    addressLabel: 'Delivery address',
    tableNumberLabel: 'Table number',
    deliveryFee: '0',
    enableOfflinePaymentDetails: false,
    bankName: '',
    accountName: '',
    accountNumber: '',
    branchCode: '',
    accountType: '',
    paymentInstructions: '',
    submitLabel: 'Send order',
    successMessage: 'Order sent.',
  },
  views: 0,
  shares: 0,
  viewKeys: [],
  shareKeys: [],
  isDraft: true,
});

const defaultShareHost = (() => {
  try {
    return new URL(DEFAULT_SHARE_ORIGIN).hostname.toLowerCase();
  } catch (error) {
    return '';
  }
})();

const isLocalHost = (host = '') => ['localhost', '127.0.0.1', '[::1]'].includes(host.toLowerCase());

const getRequestedShareTarget = () => {
  if (typeof window === 'undefined') return { customDomainHost: '', slug: '' };
  const shareParam = new URLSearchParams(window.location.search).get('share');
  if (shareParam) return { customDomainHost: '', slug: shareParam };

  const pathSlug = window.location.pathname
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .split('/')[0];

  const pathSegments = window.location.pathname
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .split('/')
    .filter(Boolean);

  if (pathSegments[0] === 'share-card' && /^[A-Za-z0-9-]+$/.test(pathSegments[1] || '')) {
    return { customDomainHost: '', slug: pathSegments[1] };
  }

  if (/^[A-Za-z0-9-]+$/.test(pathSlug)) {
    return { customDomainHost: '', slug: pathSlug };
  }

  const host = window.location.hostname;
  const isDefaultShareHost = defaultShareHost && host.toLowerCase() === defaultShareHost;
  return !isLocalHost(host) && !isDefaultShareHost
    ? { customDomainHost: host, slug: '' }
    : { customDomainHost: '', slug: '' };
};

function App() {
  const requestedShareTarget = getRequestedShareTarget();
  const requestedShareSlug = requestedShareTarget.slug;
  const requestedCustomDomainHost = requestedShareTarget.customDomainHost;
  const hasRequestedPublicShare = Boolean(requestedShareSlug || requestedCustomDomainHost);
  const [activePage, setActivePage] = useState(hasRequestedPublicShare ? 'public' : 'library');
  const [links, setLinks] = useState([]);
  const [formResponses, setFormResponses] = useState([]);
  const [itemFormResponses, setItemFormResponses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [shares, setShares] = useState([]);
  const [selectedLinkId, setSelectedLinkId] = useState(null);
  const [draftLink, setDraftLink] = useState(null);
  const [user, setUser] = useState(null);
  const [workspaceError, setWorkspaceError] = useState('');
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [isUploadingAssets, setIsUploadingAssets] = useState(false);
  const [pendingBrandFiles, setPendingBrandFiles] = useState({});
  const isFreePlan = isFreeSubscription(user);

  const loadWorkspace = useCallback(async (nextUser) => {
    setWorkspaceError('');

    if (hasRequestedPublicShare) {
      setLinks([]);
      setFormResponses([]);
      setItemFormResponses([]);
      setOrders([]);
      setInteractions([]);
      setShares([]);
      setSelectedLinkId(null);
      setDraftLink(null);
      setPendingBrandFiles({});
      setActivePage('public');

      try {
        const publicLink = requestedShareSlug
          ? await linksService.getLinkBySlug(requestedShareSlug)
          : await linksService.getLinkByCustomDomain(requestedCustomDomainHost);
        if (publicLink) {
          const hasSessionAccess = typeof window !== 'undefined' &&
            window.sessionStorage.getItem(`sharabl:share-access:${publicLink.id}`) === 'granted';
          const resolvedLink = publicLink.isProtectedContentShell && hasSessionAccess
            ? requestedShareSlug
              ? await linksService.getLinkBySlug(requestedShareSlug, { includeProtectedContent: true })
              : await linksService.getLinkByCustomDomain(requestedCustomDomainHost, { includeProtectedContent: true })
            : publicLink;

          setSelectedLinkId(resolvedLink.id);
          setDraftLink(resolvedLink);
          return;
        }
        setWorkspaceError('Share link not found.');
      } catch (error) {
        setWorkspaceError(error.message);
      }
      return;
    }

    if (!nextUser) {
      setLinks([]);
      setFormResponses([]);
      setItemFormResponses([]);
      setOrders([]);
      setInteractions([]);
      setShares([]);
      setSelectedLinkId(null);
      setDraftLink(null);
      setPendingBrandFiles({});
      return;
    }

    try {
      const [nextLinks, nextShares, nextFormResponses, nextItemFormResponses, nextOrders, nextInteractions] = await Promise.all([
        linksService.listLinks(),
        shareService.listShares(),
        formService.listResponses(),
        itemFormService.listResponses(),
        orderService.listOrders(),
        interactionService.listInteractions(),
      ]);

      await Promise.all(nextShares.map((share) => shareService.ensureAccessIndex(share)));

      setLinks(nextLinks);
      setShares(nextShares);
      setFormResponses(nextFormResponses);
      setItemFormResponses(nextItemFormResponses);
      setOrders(nextOrders);
      setInteractions(nextInteractions);

      const editableLinks = nextLinks.filter((link) => canOpenOriginalLink(nextUser, link));
      setSelectedLinkId((currentId) => (
        editableLinks.some((link) => link.id === currentId) ? currentId : editableLinks[0]?.id || null
      ));
      setDraftLink((currentDraft) => (
        currentDraft && editableLinks.some((link) => link.id === currentDraft.id)
          ? currentDraft
          : editableLinks[0] || buildDraftLink(nextUser, nextLinks.length)
      ));
    } catch (error) {
      setWorkspaceError(error.message);
    }
  }, [hasRequestedPublicShare, requestedCustomDomainHost, requestedShareSlug]);

  useEffect(() => {
    const unsubscribe = authService.onAuthChanged((nextUser) => {
      setUser(nextUser);
      loadWorkspace(nextUser);
    });
    return unsubscribe;
  }, [loadWorkspace]);

  const selectedLink = useMemo(
    () => {
      const editableLinks = links.filter((link) => canOpenOriginalLink(user, link));
      return editableLinks.find((link) => link.id === selectedLinkId) || editableLinks[0] || null;
    },
    [links, selectedLinkId, user],
  );

  useEffect(() => {
    if (selectedLink) {
      setDraftLink(selectedLink);
      setPendingBrandFiles({});
    }
  }, [selectedLink]);

  useEffect(() => {
    if (activePage === 'create' && user && !draftLink) {
      setDraftLink(buildDraftLink(user, links.length));
    }
  }, [activePage, user, draftLink, links.length]);

  const refreshLinks = async (nextSelectedId) => {
    if (!user) return;
    const nextLinks = await linksService.listLinks();
    setLinks(nextLinks);
    if (nextSelectedId) {
      const nextSelectedLink = nextLinks.find((link) => link.id === nextSelectedId);
      setSelectedLinkId(canOpenOriginalLink(user, nextSelectedLink) ? nextSelectedId : null);
    }
  };

  const handleSelectLink = (linkId) => {
    const link = links.find((item) => item.id === linkId);
    if (!canOpenOriginalLink(user, link)) {
      setWorkspaceError('Duplicate this link first to open and edit your own copy.');
      return false;
    }

    setWorkspaceError('');
    setSelectedLinkId(linkId);
    return true;
  };

  const handleCreateLink = async () => {
    setWorkspaceError('');
    if (!user) {
      setWorkspaceError('You must sign in before creating a new asset link.');
      return;
    }

    if (isFreePlan && links.length >= 1) {
      setWorkspaceError('Free plan users can create one link. Upgrade your subscription to create more links.');
      return;
    }

    const nextDraftLink = buildDraftLink(user, links.length);
    setDraftLink(isFreePlan ? applyFreePlanLinkLimits(nextDraftLink) : nextDraftLink);
    setPendingBrandFiles({});
    setSelectedLinkId(null);
    setActivePage('create');
  };

  const uploadPendingBrandImages = async (linkId, linkPayload) => {
    const nextPayload = { ...linkPayload };
    const uploadEntries = Object.entries(pendingBrandFiles).filter(([, file]) => Boolean(file));

    await Promise.all(uploadEntries.map(async ([field, file]) => {
      const uploadFile = await optimiseImageFile(file);
      nextPayload[field] = await storageService.uploadBrandImage(linkId, field, uploadFile);
      nextPayload[`${field}UploadBytes`] = uploadFile.size;
    }));

    return nextPayload;
  };

  const uploadPendingAssets = async (linkId, assets = [], { optimiseImages = true } = {}) => {
    return Promise.all(assets.map(async (asset) => {
      let nextAsset = asset;

      if (asset.iconFile) {
        const optimisedIconFile = await optimiseImageFile(asset.iconFile);
        const iconUrl = await storageService.uploadAssetIcon(linkId, asset.id, optimisedIconFile);
        const { iconFile, ...assetWithoutIconFile } = nextAsset;
        nextAsset = {
          ...assetWithoutIconFile,
          iconUrl,
          iconUploadBytes: optimisedIconFile.size,
        };
      }

      if (nextAsset.listingLogoFile) {
        const optimisedListingLogoFile = await optimiseImageFile(nextAsset.listingLogoFile);
        const listingLogoUrl = await storageService.uploadAssetIcon(linkId, `${asset.id}-listing-logo`, optimisedListingLogoFile);
        const { listingLogoFile, ...assetWithoutListingLogoFile } = nextAsset;
        nextAsset = {
          ...assetWithoutListingLogoFile,
          listingLogoUrl,
          listingLogoUploadBytes: optimisedListingLogoFile.size,
        };
      }

      if (Array.isArray(nextAsset.images) && nextAsset.images.some((image) => image.file)) {
        const nextImages = await Promise.all(nextAsset.images.map(async (image) => {
          if (!image.file) return image;

          const uploadFile = await optimiseImageFile(image.file);
          const upload = await storageService.uploadAssetFile(linkId, uploadFile);
          const { file, preview, ...imageData } = image;

          return {
            ...imageData,
            id: image.id || `image-${Date.now()}`,
            name: upload.name || uploadFile.name,
            url: upload.downloadUrl,
            downloadUrl: upload.downloadUrl,
            storagePath: upload.path,
            contentType: upload.contentType,
            uploadBytes: upload.size || uploadFile.size || 0,
          };
        }));

        nextAsset = {
          ...nextAsset,
          images: nextImages,
        };
      }

      if (!nextAsset.file) return nextAsset;

      const uploadFile = optimiseImages ? await optimiseImageFile(nextAsset.file) : nextAsset.file;
      const upload = await storageService.uploadAssetFile(linkId, uploadFile);
      const { file, ...assetData } = nextAsset;

      return {
        ...assetData,
        preview: upload.downloadUrl,
        downloadUrl: upload.downloadUrl,
        storagePath: upload.path,
        contentType: upload.contentType,
        uploadBytes: upload.size || uploadFile.size || 0,
      };
    }));
  };

  const handleSaveLink = async () => {
    if (!user || !draftLink) return false;
    setWorkspaceError('');
    setIsCreatingLink(true);
    setIsUploadingAssets(true);
    try {
      if (draftLink.id && !canOpenOriginalLink(user, draftLink)) {
        throw new Error('Duplicate this link first to open and edit your own copy.');
      }

      const { isDraft, ...linkPayload } = draftLink;
      const hasPendingBrandFiles = Object.values(pendingBrandFiles).some(Boolean);
      const limitedLinkPayload = isFreePlan ? applyFreePlanLinkLimits(linkPayload) : linkPayload;

      if (isFreePlan && !draftLink.id && links.length >= 1) {
        throw new Error('Free plan users can create one link. Upgrade your subscription to create more links.');
      }

      if (isFreePlan && hasPendingBrandFiles) {
        throw new Error('Brand images are available on paid plans.');
      }

      const pendingUploadBytes = getPendingUploadBytes(pendingBrandFiles);
      if (isFreePlan && pendingUploadBytes > FREE_PLAN_UPLOAD_LIMIT_BYTES) {
        throw new Error(`Free plan uploads are limited to ${formatFileSize(FREE_PLAN_UPLOAD_LIMIT_BYTES)} total. Your pending uploads are ${formatFileSize(pendingUploadBytes)}.`);
      }

      let saved;
      if (draftLink.id) {
        const payloadWithAssets = {
          ...limitedLinkPayload,
          assets: await uploadPendingAssets(draftLink.id, limitedLinkPayload.assets),
        };
        const finalPayload = hasPendingBrandFiles
          ? await uploadPendingBrandImages(draftLink.id, payloadWithAssets)
          : payloadWithAssets;
        saved = await linksService.updateLink(draftLink.id, finalPayload);
      } else {
        const initialPayload = {
          ...limitedLinkPayload,
          ownerId: user.uid,
          icon: pendingBrandFiles.icon ? null : limitedLinkPayload.icon,
          logo: pendingBrandFiles.logo ? null : limitedLinkPayload.logo,
          ogImage: pendingBrandFiles.ogImage ? null : limitedLinkPayload.ogImage,
          assets: [],
        };
        const created = await linksService.createLink(initialPayload);
        const payloadWithAssets = {
          ...created,
          ...limitedLinkPayload,
          assets: await uploadPendingAssets(created.id, limitedLinkPayload.assets),
        };
        const finalPayload = hasPendingBrandFiles
          ? await uploadPendingBrandImages(created.id, payloadWithAssets)
          : payloadWithAssets;
        saved = await linksService.updateLink(created.id, finalPayload);
      }

      const nextLink = saved || { ...limitedLinkPayload, updatedAt: new Date().toISOString() };
      setDraftLink(nextLink);
      setSelectedLinkId(nextLink.id);
      await refreshLinks(nextLink.id);
      setPendingBrandFiles({});
      return true;
    } catch (error) {
      setWorkspaceError(error.message);
      return false;
    } finally {
      setIsCreatingLink(false);
      setIsUploadingAssets(false);
    }
  };

  const handleUploadAssets = async (event, options = {}) => {
    if (!draftLink) return;
    if (!draftLink.id) {
      setWorkspaceError('Create the link before uploading assets.');
      event.target.value = '';
      return;
    }

    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (!files.length) return;

    setWorkspaceError('');
    setIsCreatingLink(true);
    setIsUploadingAssets(true);
    try {
      const nextUploadBytes = getLinkUploadBytes(draftLink) + files.reduce((total, file) => total + file.size, 0);
      if (isFreePlan && nextUploadBytes > FREE_PLAN_UPLOAD_LIMIT_BYTES) {
        throw new Error(`Free plan uploads are limited to ${formatFileSize(FREE_PLAN_UPLOAD_LIMIT_BYTES)} total. After processing, your selected files would bring this link to ${formatFileSize(nextUploadBytes)}.`);
      }

      const localAssets = assetsService.fromFiles(files);
      const uploadedAssets = await uploadPendingAssets(draftLink.id, localAssets);
      const nextLink = addAssetsToGrid({
        ...(isFreePlan ? applyFreePlanLinkLimits(draftLink) : draftLink),
        assets: [...uploadedAssets, ...(draftLink.assets || [])],
      }, uploadedAssets, options.gridId);
      const saved = await linksService.updateLink(draftLink.id, nextLink);
      setDraftLink(saved);
      await refreshLinks(saved.id);
    } catch (error) {
      setWorkspaceError(error.message);
    } finally {
      setIsCreatingLink(false);
      setIsUploadingAssets(false);
    }
  };

  const handleAddManualAsset = async (asset, options = {}) => {
    if (!draftLink || !asset) return;
    let nextAsset = asset;

    if (draftLink.id && asset.iconFile) {
      try {
        const nextUploadBytes = getLinkUploadBytes(draftLink) + asset.iconFile.size;
        if (isFreePlan && nextUploadBytes > FREE_PLAN_UPLOAD_LIMIT_BYTES) {
          throw new Error(`Free plan uploads are limited to ${formatFileSize(FREE_PLAN_UPLOAD_LIMIT_BYTES)} total. This icon would bring the link to ${formatFileSize(nextUploadBytes)}.`);
        }

        const iconUrl = await storageService.uploadAssetIcon(draftLink.id, asset.id, asset.iconFile);
        const { iconFile, ...assetWithoutIconFile } = asset;
        nextAsset = {
          ...assetWithoutIconFile,
          iconUrl,
          iconUploadBytes: asset.iconFile.size,
        };
      } catch (error) {
        setWorkspaceError(error.message);
        return;
      }
    }

    const nextLink = addAssetsToGrid({
      ...(isFreePlan ? applyFreePlanLinkLimits(draftLink) : draftLink),
      assets: [nextAsset, ...(draftLink.assets || [])],
    }, [nextAsset], options.gridId);

    setWorkspaceError('');
    setDraftLink(nextLink);

    if (!draftLink.id) return;

    setIsCreatingLink(true);
    try {
      const saved = await linksService.updateLink(draftLink.id, nextLink);
      setDraftLink(saved);
      await refreshLinks(saved.id);
    } catch (error) {
      setWorkspaceError(error.message);
      setDraftLink(draftLink);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleAssetChange = (nextAsset) => {
    if (!draftLink) return;
    setDraftLink({
      ...draftLink,
      assets: (draftLink.assets || []).map((asset) => (
        asset.id === nextAsset.id ? nextAsset : asset
      )),
    });
  };

  const handleAssetDelete = async (assetToDelete) => {
    if (!draftLink || !assetToDelete) return;
    const shouldDelete = window.confirm(`Delete "${assetToDelete.title || 'this asset'}" from this link?`);
    if (!shouldDelete) return;

    const nextLink = removeAssetFromGrids({
      ...draftLink,
      assets: (draftLink.assets || []).filter((asset) => asset.id !== assetToDelete.id),
    }, assetToDelete.id);

    setWorkspaceError('');
    setDraftLink(nextLink);

    if (!draftLink.id) return;

    setIsCreatingLink(true);
    try {
      const saved = await linksService.updateLink(draftLink.id, nextLink);
      setDraftLink(saved);
      await refreshLinks(saved.id);
    } catch (error) {
      setWorkspaceError(error.message);
      setDraftLink(draftLink);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleDeleteLink = async (link) => {
    if (!user || !link?.id) return;
    if (!canOpenOriginalLink(user, link)) {
      setWorkspaceError('Duplicate this link first to manage your own copy.');
      return;
    }

    setWorkspaceError('');
    setIsCreatingLink(true);
    try {
      await linksService.removeLink(link.id);
      const nextLinks = links.filter((item) => item.id !== link.id);
      setLinks(nextLinks);
      setFormResponses((currentResponses) => currentResponses.filter((response) => response.linkId !== link.id));
      setItemFormResponses((currentResponses) => currentResponses.filter((response) => response.linkId !== link.id));
      setOrders((currentOrders) => currentOrders.filter((order) => order.linkId !== link.id));
      setInteractions((currentInteractions) => currentInteractions.filter((interaction) => interaction.linkId !== link.id));
      setSelectedLinkId((currentId) => (currentId === link.id ? nextLinks[0]?.id || null : currentId));
      setDraftLink((currentDraft) => (
        currentDraft?.id === link.id
          ? nextLinks[0] || buildDraftLink(user, nextLinks.length)
          : currentDraft
      ));
    } catch (error) {
      setWorkspaceError(error.message);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleDuplicateLink = async (link) => {
    if (!user || !link?.id) return;

    setWorkspaceError('');
    setIsCreatingLink(true);
    try {
      const duplicatedLink = await linksService.duplicateLink(link, { allowAdminOverride: isAdminUser(user) });
      setLinks((currentLinks) => [duplicatedLink, ...currentLinks]);
      setSelectedLinkId(duplicatedLink.id);
      setDraftLink(duplicatedLink);
      setPendingBrandFiles({});
      await refreshLinks(duplicatedLink.id);
    } catch (error) {
      setWorkspaceError(error.message);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleUpdateLinkStatus = async (link, status) => {
    if (!user || !link?.id || !status || link.status === status) return;
    if (!canOpenOriginalLink(user, link)) {
      setWorkspaceError('Duplicate this link first to manage your own copy.');
      return;
    }

    setWorkspaceError('');
    try {
      const nextLink = { ...link, status };
      setLinks((currentLinks) => currentLinks.map((item) => (
        item.id === link.id ? nextLink : item
      )));
      setDraftLink((currentDraft) => (
        currentDraft?.id === link.id ? { ...currentDraft, status } : currentDraft
      ));

      const saved = await linksService.updateLink(link.id, nextLink);
      if (saved) {
        setLinks((currentLinks) => currentLinks.map((item) => (
          item.id === saved.id ? saved : item
        )));
        setDraftLink((currentDraft) => (
          currentDraft?.id === saved.id ? saved : currentDraft
        ));
      }
    } catch (error) {
      setWorkspaceError(error.message);
      await refreshLinks(link.id);
    }
  };

  const handleUpdateOrder = async (order, changes) => {
    if (!user || !order?.id) return;

    setWorkspaceError('');
    const nextOrder = { ...order, ...changes };
    setOrders((currentOrders) => currentOrders.map((item) => (
      item.id === order.id ? nextOrder : item
    )));

    try {
      const savedChanges = await orderService.updateOrder(order, changes);
      setOrders((currentOrders) => currentOrders.map((item) => (
        item.id === order.id ? { ...item, ...savedChanges } : item
      )));
      if (
        orderStatusCanAffectStock(order, changes.status) ||
        (orderStatusCanAffectStock(order, order.status) && changes.status === 'Cancelled')
      ) {
        await refreshLinks(order.linkId);
      }
    } catch (error) {
      setWorkspaceError(error.message);
      setOrders((currentOrders) => currentOrders.map((item) => (
        item.id === order.id ? order : item
      )));
    }
  };

  const handleUpdateItemFormResponse = async (response, changes) => {
    if (!user || !response?.id) return;

    setWorkspaceError('');
    const nextResponse = { ...response, ...changes };
    setItemFormResponses((currentResponses) => currentResponses.map((item) => (
      item.id === response.id ? nextResponse : item
    )));

    try {
      const savedChanges = await itemFormService.updateResponse(response, changes);
      setItemFormResponses((currentResponses) => currentResponses.map((item) => (
        item.id === response.id ? { ...item, ...savedChanges } : item
      )));
      if (
        changes.receiving?.status === 'Confirmed' ||
        (response.receiving?.status === 'Confirmed' && changes.receiving?.status === 'Cancelled')
      ) {
        await refreshLinks(response.linkId);
      }
    } catch (error) {
      setWorkspaceError(error.message);
      setItemFormResponses((currentResponses) => currentResponses.map((item) => (
        item.id === response.id ? response : item
      )));
    }
  };

  const handleRemoveSubmission = async (submission) => {
    if (!user || !submission?.id) return;

    setWorkspaceError('');
    const submissionType = submission.type;

    if (submissionType === 'response') {
      const previousResponses = formResponses;
      setFormResponses((currentResponses) => currentResponses.filter((item) => item.id !== submission.id));

      try {
        await formService.removeResponse(submission);
      } catch (error) {
        setWorkspaceError(error.message);
        setFormResponses(previousResponses);
      }
      return;
    }

    if (submissionType === 'itemForm') {
      const previousResponses = itemFormResponses;
      setItemFormResponses((currentResponses) => currentResponses.filter((item) => item.id !== submission.id));

      try {
        await itemFormService.removeResponse(submission);
        if (submission.receiving?.status === 'Confirmed') {
          await refreshLinks(submission.linkId);
        }
      } catch (error) {
        setWorkspaceError(error.message);
        setItemFormResponses(previousResponses);
      }
      return;
    }

    if (submissionType === 'order') {
      const previousOrders = orders;
      setOrders((currentOrders) => currentOrders.filter((item) => item.id !== submission.id));

      try {
        await orderService.removeOrder(submission);
        if (submission.status === 'Confirmed') {
          await refreshLinks(submission.linkId);
        }
      } catch (error) {
        setWorkspaceError(error.message);
        setOrders(previousOrders);
      }
    }
  };

  const persistAssetOrder = async (nextLink, previousLink) => {
    setWorkspaceError('');
    setDraftLink(nextLink);

    if (!previousLink.id) return;

    setIsCreatingLink(true);
    try {
      const saved = await linksService.updateLink(previousLink.id, nextLink);
      setDraftLink(saved);
      await refreshLinks(saved.id);
    } catch (error) {
      setWorkspaceError(error.message);
      setDraftLink(previousLink);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleAssetMove = async (fromIndex, toIndex) => {
    if (!draftLink) return;
    const currentAssets = draftLink.assets || [];
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= currentAssets.length ||
      toIndex >= currentAssets.length
    ) {
      return;
    }

    const nextAssets = [...currentAssets];
    const [movedAsset] = nextAssets.splice(fromIndex, 1);
    nextAssets.splice(toIndex, 0, movedAsset);

    await persistAssetOrder({
      ...draftLink,
      assets: nextAssets,
    }, draftLink);
  };

  const handleCreateShare = async (share) => {
    if (!user) return;
    const nextShare = share.requestId
      ? await shareService.grantInviteRequest(share.requestId, share)
      : await shareService.createShare(share);
    if (share.linkId) {
      const uniqueShareKey = [
        'recipient',
        share.allowedEmails?.join(',') || share.recipient || 'copied link',
        share.channel || 'Copy link',
      ].join(':');

      try {
        await linkAnalyticsService.trackUniqueShare(share.linkId, uniqueShareKey);
        await refreshLinks(share.linkId);
      } catch (error) {
        console.warn('Unable to track dashboard share.', error);
      }
    }
    setShares((current) => {
      if (share.requestId) {
        return current.map((item) => (item.id === share.requestId ? { ...item, ...nextShare } : item));
      }

      return [
        ...(Array.isArray(nextShare) ? nextShare : [nextShare]),
        ...current,
      ];
    });
    return nextShare;
  };

  const handlePublicAccessGranted = useCallback(async (slug) => {
    if (!slug) return;
    const fullLink = await linksService.getLinkBySlug(slug, { includeProtectedContent: true });
    if (fullLink) {
      setSelectedLinkId(fullLink.id);
      setDraftLink(fullLink);
    }
  }, []);

  const handleOpenCreateFromPublic = () => {
    if (user) {
      handleCreateLink();
      return;
    }

    setWorkspaceError('');
    setDraftLink(null);
    setSelectedLinkId(null);
    setActivePage('create');
  };

  const pageProps = {
    links,
    formResponses,
    itemFormResponses,
    orders,
    interactions,
    shares,
    activeLinkId: selectedLinkId,
    onSelectLink: handleSelectLink,
    onNavigate: setActivePage,
  };

  if (activePage === 'public') {
    return (
      <PublicSharePage
        error={workspaceError}
        link={draftLink || selectedLink}
        onAccessGranted={handlePublicAccessGranted}
        onCreateLink={handleOpenCreateFromPublic}
        user={user}
      />
    );
  }

  return (
    <AppShell activePage={activePage} onNavigate={setActivePage} user={user}>
      {activePage === 'library' && (
        <LibraryPage
          {...pageProps}
          error={workspaceError}
          isCreating={isCreatingLink}
          isSignedIn={Boolean(user)}
          user={user}
          onCreateLink={handleCreateLink}
          onDeleteLink={handleDeleteLink}
          onDuplicateLink={handleDuplicateLink}
          onItemFormResponseChange={handleUpdateItemFormResponse}
          onOrderChange={handleUpdateOrder}
          onSubmissionDelete={handleRemoveSubmission}
          onStatusChange={handleUpdateLinkStatus}
        />
      )}

      {activePage === 'create' && (
        <LinkCreationPage
          link={draftLink}
          error={workspaceError}
          isUploadingAssets={isUploadingAssets}
          isSaving={isCreatingLink}
          isSignedIn={Boolean(user)}
          isFreePlan={isFreePlan}
          uploadLimitBytes={isFreePlan ? FREE_PLAN_UPLOAD_LIMIT_BYTES : null}
          uploadUsedBytes={getLinkUploadBytes(draftLink, pendingBrandFiles)}
          onChange={(nextLink, brandFileChange) => {
            setDraftLink(isFreePlan ? applyFreePlanLinkLimits(nextLink) : nextLink);
            if (brandFileChange) {
              setPendingBrandFiles((current) => {
                if (brandFileChange.file) {
                  return {
                    ...current,
                    [brandFileChange.field]: brandFileChange.file,
                  };
                }

                const remainingFiles = { ...current };
                delete remainingFiles[brandFileChange.field];
                return remainingFiles;
              });
            }
          }}
          onAddManualAsset={handleAddManualAsset}
          onOpenLiveShare={() => setActivePage('public')}
          onAssetChange={handleAssetChange}
          onAssetDelete={handleAssetDelete}
          onAssetMove={handleAssetMove}
          onSave={handleSaveLink}
          onUploadAssets={handleUploadAssets}
        />
      )}

      {activePage === 'shares' && (
        <SharesPage
          links={links}
          shares={shares}
          onCreateShare={handleCreateShare}
        />
      )}

      {activePage === 'admin' && (
        <AdminPage user={user} />
      )}

    </AppShell>
  );
}

export default App;
